import React, { useState, useEffect, useRef, useCallback } from 'react';
import { BRAND } from './statuses';
import type { Assignee } from './ticketApi';
import { fetchAssignees } from './ticketApi';

interface Props {
  currentAssigneeId: string | null;
  currentAssigneeName: string | null;
  onChange: (id: string | null) => void;
  disabled?: boolean;
}

export const AssigneeDropdown: React.FC<Props> = ({
  currentAssigneeId,
  currentAssigneeName,
  onChange,
  disabled,
}) => {
  const [open, setOpen] = useState(false);
  const [assignees, setAssignees] = useState<Assignee[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    if (assignees !== null) return;
    setLoading(true);
    try {
      const list = await fetchAssignees();
      setAssignees(list);
    } catch {
      setAssignees([]);
    } finally {
      setLoading(false);
    }
  }, [assignees]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = (assignees || []).filter((a) => {
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return (
      (a.name || '').toLowerCase().includes(q) ||
      a.email.toLowerCase().includes(q)
    );
  });

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <button
        onClick={() => !disabled && setOpen(!open)}
        disabled={disabled}
        style={{
          width: '100%',
          padding: '8px 12px',
          background: BRAND.surface,
          border: `1px solid ${BRAND.border}`,
          borderRadius: 8,
          fontSize: 13,
          color: currentAssigneeId ? BRAND.text : BRAND.textMuted,
          cursor: disabled ? 'not-allowed' : 'pointer',
          textAlign: 'left',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <span>{currentAssigneeName || 'Unassigned'}</span>
        <span style={{ color: BRAND.textMuted, fontSize: 10 }}>▾</span>
      </button>

      {open && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            marginTop: 4,
            background: BRAND.surface,
            border: `1px solid ${BRAND.border}`,
            borderRadius: 8,
            boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
            zIndex: 50,
            maxHeight: 300,
            overflowY: 'auto',
          }}
        >
          <div style={{ padding: 8, borderBottom: `1px solid ${BRAND.borderSoft}` }}>
            <input
              type="text"
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search team..."
              style={{
                width: '100%',
                padding: '6px 10px',
                border: `1px solid ${BRAND.border}`,
                borderRadius: 6,
                fontSize: 12,
                boxSizing: 'border-box',
              }}
            />
          </div>

          <button
            onClick={() => {
              onChange(null);
              setOpen(false);
            }}
            style={{
              width: '100%',
              padding: '10px 14px',
              textAlign: 'left',
              border: 'none',
              background: !currentAssigneeId ? BRAND.surfaceAlt : 'transparent',
              cursor: 'pointer',
              fontSize: 13,
              color: BRAND.textMuted,
              fontStyle: 'italic',
              borderBottom: `1px solid ${BRAND.borderSoft}`,
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = BRAND.surfaceAlt)}
            onMouseLeave={(e) => {
              if (currentAssigneeId) e.currentTarget.style.background = 'transparent';
            }}
          >
            Unassigned
          </button>

          {loading && (
            <div style={{ padding: 14, fontSize: 12, color: BRAND.textMuted, textAlign: 'center' }}>
              Loading…
            </div>
          )}
          {!loading &&
            filtered.map((a) => {
              const active = a.id === currentAssigneeId;
              return (
                <button
                  key={a.id}
                  onClick={() => {
                    onChange(a.id);
                    setOpen(false);
                  }}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    textAlign: 'left',
                    border: 'none',
                    background: active ? BRAND.surfaceAlt : 'transparent',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = BRAND.surfaceAlt)}
                  onMouseLeave={(e) => {
                    if (!active) e.currentTarget.style.background = 'transparent';
                  }}
                >
                  <span style={{ fontSize: 13, fontWeight: 600, color: BRAND.text }}>
                    {a.name || a.email}
                  </span>
                  <span style={{ fontSize: 11, color: BRAND.textMuted }}>
                    {a.email} · {a.role}
                  </span>
                </button>
              );
            })}
          {!loading && filtered.length === 0 && (
            <div style={{ padding: 14, fontSize: 12, color: BRAND.textMuted, textAlign: 'center' }}>
              No team members match
            </div>
          )}
        </div>
      )}
    </div>
  );
};
